import { useCallback, useEffect, useState } from 'react';
import type { MembroDaEquipe } from '../../acesso';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import {
  MODELOS_EMPILHADO,
  MODELOS_PARTES,
  MODELOS_TEMPO,
  empilhado,
  fmt,
  partes,
  serieNoTempo,
} from '../../design/graficos/modelos';
import { hojeBrasilia, lerLucroDoInovapay } from '../../paineis';
import type { LucroDoInovapay, ProgramaDoLucro, TotaisDoLucro } from '../../paineis';
import {
  BarraDeFiltros,
  Carregando,
  Kpi,
  diaCurto,
  irPara,
  lerFiltros,
  nomeCurto,
  textoDoPeriodo,
} from '../paineis/pecasDaVisao';
import '../central/central.css';
import './lucro.css';

/**
 * LUCRO DO INOVAPAY — quanto a FARMÁCIA ganhou com vitamina, pré-vencido e paradão: o "Lucro da
 * Farmácia" do 1.0, agora por período, por balconista e com a evolução dia a dia. Dado da LOJA
 * (custo e lucro): a rota e o menu exigem `loja.ver` — só o dono vê.
 */

const ROTA = 'lucro-inovapay';
const PROGRAMAS: readonly { id: ProgramaDoLucro; nome: string; cor: string }[] = [
  { id: 'vitamina', nome: 'Vitamina', cor: '#1a7f55' },
  { id: 'pre_vencido', nome: 'Pré-vencido', cor: '#cb1b2b' },
  { id: 'paradao', nome: 'Paradão', cor: '#b7791f' },
];
const r$ = (v: number | null) => (v === null ? '—' : fmt(v, 'reais'));
const pct = (a: number, b: number) => (b ? (a / b) * 100 : null);
const tom = (v: number | null) => (v === null ? '' : v < 0 ? ' lc-neg' : v > 0 ? ' lc-pos' : '');

export function LucroDoInovapayTela({ parametros }: { parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  const f = lerFiltros(parametros, hoje);
  const [d, setD] = useState<LucroDoInovapay | null>(null);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerLucroDoInovapay(f.periodo, f.pessoa).then((r) =>
      r.ok ? setD(r.dados) : setErro(true),
    );
  }, [f.periodo.de, f.periodo.ate, f.pessoa]);
  useEffect(carregar, [carregar]);

  const equipe: MembroDaEquipe[] = (d?.balconistas ?? []).map((b) => ({
    personId: b.accountId,
    accountId: b.accountId,
    pedidoId: null,
    nome: b.nome,
    login: null,
    perfis: [],
    situacao: 'ativo',
    suspensoAte: null,
    ultimoAcesso: null,
  }));
  const t = d?.total;
  const nomeDaPessoa = f.pessoa ? d?.balconistas.find((b) => b.accountId === f.pessoa)?.nome : null;
  const trilha = (k: 'vendido' | 'lucroLiquido' | 'premiacao') => d?.porDia.map((x) => x[k]);
  const temDado = !!t && t.itens > 0;

  return (
    <section className="cv lc">
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <div className="vg-saudacao">Loja · só o dono vê</div>
            <h1>Lucro do InovaPay</h1>
            <div className="vg-contexto">
              O que a farmácia ganhou com vitamina, pré-vencido e paradão ·{' '}
              {textoDoPeriodo(f.filtro, f.periodo, hoje)}
              {nomeDaPessoa ? ` · ${nomeCurto(nomeDaPessoa)}` : ''}
            </div>
          </div>
          <div className="vg-acoes">
            <button type="button" className="vg-botao" onClick={() => window.print()}>
              <Icone nome="file" tamanho={14} traco={1.7} />
              Imprimir
            </button>
          </div>
        </header>

        <BarraDeFiltros f={f} hoje={hoje} rota={ROTA} equipe={d ? equipe : undefined} />

        {erro ? (
          <ErroDeArea oQue="o lucro do InovaPay" aoTentar={carregar} />
        ) : (
          <>
            <div className="vg-kpis">
              <Kpi
                rotulo="Vendido pelos programas"
                icone="receipt"
                valor={t ? r$(t.vendido) : null}
                variacao={null}
                sub={t ? `${t.itens.toLocaleString('pt-BR')} itens lançados e ativos` : null}
                trilha={trilha('vendido')}
                link={`#/${ROTA}`}
              />
              <Kpi
                rotulo="Lucro líquido"
                icone="money"
                valor={t ? r$(t.lucroLiquido) : null}
                variacao={null}
                sub={
                  t
                    ? `margem líquida ${fmt(pct(t.lucroLiquido, t.vendido) ?? 0, 'pct')} · já sem custo e prêmio`
                    : null
                }
                trilha={trilha('lucroLiquido')}
                link={`#/${ROTA}`}
                destaque
              />
              <Kpi
                rotulo="Lucro extra do app"
                icone="up-t"
                valor={t ? r$(t.lucroExtraDoApp) : null}
                variacao={null}
                sub={t ? 'acima do preço base, já pago o prêmio' : null}
                link={`#/${ROTA}`}
              />
              <Kpi
                rotulo="Parte da farmácia"
                icone="shield"
                valor={t ? r$(t.parteDaFarmacia) : null}
                variacao={null}
                sub={
                  t && t.ganhoExtra
                    ? `${fmt(pct(t.parteDaFarmacia ?? 0, t.ganhoExtra) ?? 0, 'pct')} do aumento sobre a base`
                    : t
                      ? 'sem venda acima da base'
                      : null
                }
                link={`#/${ROTA}`}
              />
              <Kpi
                rotulo="Premiação paga"
                icone="trophy"
                valor={t ? r$(t.premiacao) : null}
                variacao={null}
                sub={t ? `${fmt(pct(t.premiacao, t.vendido) ?? 0, 'pct')} do vendido` : null}
                trilha={trilha('premiacao')}
                link={`#/${ROTA}`}
              />
              <Kpi
                rotulo="Custo dos itens"
                icone="box"
                valor={t ? r$(t.custo) : null}
                variacao={null}
                sub={
                  t
                    ? t.semCusto
                      ? `${String(t.semCusto)} item(ns) sem custo no cadastro`
                      : 'todos com custo'
                    : null
                }
                link={`#/${ROTA}`}
              />
            </div>

            {t && t.semCusto > 0 && (
              <div className="cv-aviso">
                <Icone nome="warn" tamanho={15} traco={1.7} />
                <span>
                  {t.semCusto} lançamento(s) sem custo confiável (vazio ou abaixo de R$ 0,05) entram
                  com custo zero — o lucro desses itens está <strong>acima do real</strong>. Acerte
                  o custo no HOS para a conta fechar.
                </span>
              </div>
            )}

            {!d ? (
              <Carregando />
            ) : !temDado ? (
              <p className="cv-vazio">
                Nenhum lançamento ativo de vitamina, pré-vencido ou paradão neste período.
              </p>
            ) : (
              <>
                <TabelaPorPrograma d={d} />
                <div className="gx-grade">
                  <DivisaoDoAumento t={d.total} />
                  <Podio d={d} f={f} />
                </div>
                <div className="gx-grade">
                  <CartaoDeGrafico
                    id="lc-dia"
                    titulo="Lucro líquido dia a dia"
                    numero={r$(d.total.lucroLiquido)}
                    conclusao={(() => {
                      const melhor = [...d.porDia].sort(
                        (a, b) => b.lucroLiquido - a.lucroLiquido,
                      )[0];
                      return melhor && melhor.lucroLiquido > 0 ? (
                        <>
                          Melhor dia: <strong>{diaCurto(melhor.dia)}</strong>, com{' '}
                          {fmt(melhor.lucroLiquido, 'reais')} de lucro sobre{' '}
                          {fmt(melhor.vendido, 'reais')} vendidos.
                        </>
                      ) : (
                        'Sem lucro positivo em nenhum dia do período.'
                      );
                    })()}
                    modelos={MODELOS_TEMPO}
                    padrao="combo"
                    desenhar={(m) =>
                      serieNoTempo(m, {
                        cats: d.porDia.map((x) => diaCurto(x.dia)),
                        atual: d.porDia.map((x) => x.lucroLiquido),
                        anterior: d.porDia.map(() => null),
                        nome: 'Lucro líquido',
                        formato: 'reais',
                        linha: {
                          nome: 'Vendido',
                          valores: d.porDia.map((x) => x.vendido),
                          formato: 'reais',
                        },
                      })
                    }
                    fonte="Fonte: lançamentos ativos × custo do dia da venda"
                    vazio={
                      d.porDia.length < 2
                        ? 'Escolha um período de mais de um dia para ver a evolução.'
                        : null
                    }
                    altura={280}
                  />
                  <CartaoDeGrafico
                    id="lc-programa"
                    titulo="De onde vem o lucro"
                    numero={r$(d.total.lucroLiquido)}
                    conclusao={(() => {
                      const lider = PROGRAMAS.map((p) => ({
                        ...p,
                        v: d.porPrograma[p.id].lucroLiquido,
                      })).sort((a, b) => b.v - a.v)[0];
                      return lider && lider.v > 0 ? (
                        <>
                          <strong>{lider.nome}</strong> responde por{' '}
                          {fmt(pct(lider.v, d.total.lucroLiquido) ?? 0, 'pct')} do lucro líquido.
                        </>
                      ) : (
                        'Nenhum programa deu lucro no período.'
                      );
                    })()}
                    modelos={MODELOS_EMPILHADO}
                    padrao="colunas_empilhadas"
                    desenhar={(m) =>
                      empilhado(m, {
                        cats: PROGRAMAS.map((p) => p.nome),
                        series: [
                          {
                            nome: 'Custo',
                            valores: PROGRAMAS.map((p) => d.porPrograma[p.id].custo),
                            cor: '#b9c0c6',
                          },
                          {
                            nome: 'Premiação',
                            valores: PROGRAMAS.map((p) => d.porPrograma[p.id].premiacao),
                            cor: '#b7791f',
                          },
                          {
                            nome: 'Lucro líquido',
                            valores: PROGRAMAS.map((p) => d.porPrograma[p.id].lucroLiquido),
                            cor: '#1a7f55',
                          },
                        ],
                        formato: 'reais',
                      })
                    }
                    fonte="Cada coluna é o vendido do programa: custo + premiação + lucro"
                    altura={280}
                  />
                </div>
                {!f.pessoa && d.porBalconista.length > 1 && (
                  <CartaoDeGrafico
                    id="lc-balconista"
                    titulo="Lucro líquido por balconista"
                    numero={d.porBalconista[0] ? nomeCurto(d.porBalconista[0].nome) : '—'}
                    conclusao={`${String(d.porBalconista.length)} balconista(s) com lançamento no período; clique na tabela abaixo para ver só um.`}
                    modelos={MODELOS_PARTES}
                    padrao="barras"
                    desenhar={(m) =>
                      partes(m, {
                        itens: d.porBalconista
                          .slice(0, 15)
                          .map((p) => ({ nome: nomeCurto(p.nome), valor: p.lucroLiquido })),
                        formato: 'reais',
                      })
                    }
                    fonte="Fonte: lançamentos ativos de cada balconista"
                    altura={300}
                  />
                )}
                <TabelaPorBalconista d={d} f={f} />
                <div className="cv-nota">
                  <p>
                    <strong>Como a conta é feita</strong> (só itens lançados e ativos — cancelados e
                    desfeitos ficam fora):
                  </p>
                  <p>
                    <strong>Vendido</strong> = quantidade × preço lançado · <strong>Custo</strong> =
                    quantidade × custo do dia da venda (sem ele, o do cadastro) ·{' '}
                    <strong>Se vendesse no base</strong> = quantidade × preço base (pré-vencido e
                    paradão; vitamina não tem base).
                  </p>
                  <p>
                    <strong>Ganho extra</strong> = vendido − se vendesse no base ·{' '}
                    <strong>Lucro líquido</strong> = vendido − custo − premiação ·{' '}
                    <strong>Lucro extra do app</strong> = ganho extra − premiação desses itens ·{' '}
                    <strong>Parte da farmácia</strong> = ganho extra − a fatia do aumento que virou
                    prêmio do atendente.
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Linha({
  nome,
  x,
  cor,
  forte,
}: {
  nome: string;
  x: TotaisDoLucro;
  cor?: string;
  forte?: boolean;
}) {
  return (
    <tr className={forte ? 'lc-total' : undefined}>
      <td>
        {cor && <i className="lc-ponto" style={{ background: cor }} />}
        {nome}
      </td>
      <td className="cv-dir">{x.itens.toLocaleString('pt-BR')}</td>
      <td className="cv-dir">{r$(x.custo)}</td>
      <td className="cv-dir">{r$(x.seVendesseNoBase)}</td>
      <td className="cv-dir">{r$(x.vendido)}</td>
      <td className={`cv-dir${tom(x.ganhoExtra)}`}>{r$(x.ganhoExtra)}</td>
      <td className="cv-dir">{r$(x.premiacao)}</td>
      <td className={`cv-dir lc-forte${tom(x.lucroLiquido)}`}>{r$(x.lucroLiquido)}</td>
      <td className={`cv-dir${tom(x.lucroExtraDoApp)}`}>{r$(x.lucroExtraDoApp)}</td>
    </tr>
  );
}

function TabelaPorPrograma({ d }: { d: LucroDoInovapay }) {
  return (
    <div className="vg-bloco cv-lista">
      <div className="lc-cabeca">
        <h2>Por programa</h2>
        <p>A mesma leitura do "Lucro da Farmácia" do 1.0, com o custo do dia da venda.</p>
      </div>
      <div className="cv-rolagem">
        <table className="cv-tabela">
          <thead>
            <tr>
              <th>Programa</th>
              <th className="cv-dir">Itens</th>
              <th className="cv-dir">Custo</th>
              <th className="cv-dir">Se vendesse no base</th>
              <th className="cv-dir">Vendido</th>
              <th className="cv-dir">Ganho extra</th>
              <th className="cv-dir">Premiação</th>
              <th className="cv-dir">Lucro líquido</th>
              <th className="cv-dir">Lucro extra do app</th>
            </tr>
          </thead>
          <tbody>
            {PROGRAMAS.map((p) => (
              <Linha key={p.id} nome={p.nome} cor={p.cor} x={d.porPrograma[p.id]} />
            ))}
            <Linha nome="Total" x={d.total} forte />
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DivisaoDoAumento({ t }: { t: TotaisDoLucro }) {
  const extra = t.ganhoExtra ?? 0;
  const farmacia = t.parteDaFarmacia ?? 0;
  const atendente = t.parteDoAtendente ?? 0;
  const pf = extra > 0 ? Math.max(0, Math.min(100, (farmacia / extra) * 100)) : 0;
  return (
    <div className="gx-cartao lc-divisao">
      <h3 className="lc-h3">Divisão do aumento sobre a base</h3>
      <p className="lc-sub">
        Pré-vencido e paradão: o que foi vendido acima do preço base é dividido entre a farmácia e o
        atendente.
      </p>
      {extra > 0 ? (
        <>
          <div className="lc-numero">{r$(extra)}</div>
          <div
            className="lc-barra"
            role="img"
            aria-label={`Farmácia ${fmt(pf, 'pct')}, atendente ${fmt(100 - pf, 'pct')}`}
          >
            <i className="lc-barra-farmacia" style={{ width: `${String(pf)}%` }} />
            <i className="lc-barra-atendente" style={{ width: `${String(100 - pf)}%` }} />
          </div>
          <div className="lc-legenda">
            <span>
              <i className="lc-barra-farmacia" /> Farmácia <strong>{r$(farmacia)}</strong> ·{' '}
              {fmt(pf, 'pct')}
            </span>
            <span>
              <i className="lc-barra-atendente" /> Atendente <strong>{r$(atendente)}</strong> ·{' '}
              {fmt(100 - pf, 'pct')}
            </span>
          </div>
          <p className="lc-sub">
            Se tudo tivesse sido vendido no preço base, a loja teria feito {r$(t.seVendesseNoBase)}{' '}
            nesses itens.
          </p>
        </>
      ) : (
        <p className="cv-vazio">Nenhuma venda acima do preço base neste período.</p>
      )}
    </div>
  );
}

function Podio({ d, f }: { d: LucroDoInovapay; f: ReturnType<typeof lerFiltros> }) {
  const top = d.porBalconista.slice(0, 3);
  const ordem = [top[1], top[0], top[2]];
  return (
    <div className="gx-cartao">
      <h3 className="lc-h3">Pódio do lucro</h3>
      <p className="lc-sub">Quem mais deu lucro líquido para a farmácia com os programas.</p>
      {top.length === 0 ? (
        <p className="cv-vazio">Ninguém lançou neste período.</p>
      ) : (
        <div className="lc-podio">
          {ordem.map((p, i) =>
            p ? (
              <button
                key={p.accountId}
                type="button"
                className={`lc-degrau lc-degrau-${String(i === 1 ? 1 : i === 0 ? 2 : 3)}`}
                onClick={() => irPara({ ...f, pessoa: p.accountId }, ROTA)}
              >
                <span className="lc-posicao">{i === 1 ? 1 : i === 0 ? 2 : 3}º</span>
                <strong>{nomeCurto(p.nome)}</strong>
                <span>{r$(p.lucroLiquido)}</span>
                <em>{p.itens.toLocaleString('pt-BR')} itens</em>
              </button>
            ) : (
              <span key={`vazio-${String(i)}`} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function TabelaPorBalconista({ d, f }: { d: LucroDoInovapay; f: ReturnType<typeof lerFiltros> }) {
  return (
    <div className="vg-bloco cv-lista">
      <div className="lc-cabeca">
        <h2>Por balconista</h2>
        <p>
          {f.pessoa ? 'Só a pessoa escolhida.' : 'Clique numa pessoa para ver só a conta dela.'}{' '}
          Itens: vitamina · pré-vencido · paradão.
        </p>
      </div>
      <div className="cv-rolagem">
        <table className="cv-tabela">
          <thead>
            <tr>
              <th>#</th>
              <th>Balconista</th>
              <th className="cv-dir">Itens (V · PV · P)</th>
              <th className="cv-dir">Vendido</th>
              <th className="cv-dir">Custo</th>
              <th className="cv-dir">Premiação</th>
              <th className="cv-dir">Lucro líquido</th>
              <th className="cv-dir">Lucro extra do app</th>
              <th className="cv-dir">Parte da farmácia</th>
            </tr>
          </thead>
          <tbody>
            {d.porBalconista.map((p, i) => (
              <tr
                key={p.accountId}
                className="lc-clicavel"
                tabIndex={0}
                onClick={() => irPara({ ...f, pessoa: f.pessoa ? null : p.accountId }, ROTA)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && irPara({ ...f, pessoa: f.pessoa ? null : p.accountId }, ROTA)
                }
              >
                <td className="ac-fraco">{i + 1}</td>
                <td>
                  <strong>{nomeCurto(p.nome)}</strong>
                  <span className="lc-codigo"> · {p.codigo}</span>
                </td>
                <td className="cv-dir">
                  {p.porPrograma.vitamina} · {p.porPrograma.pre_vencido} · {p.porPrograma.paradao}
                </td>
                <td className="cv-dir">{r$(p.vendido)}</td>
                <td className="cv-dir">{r$(p.custo)}</td>
                <td className="cv-dir">{r$(p.premiacao)}</td>
                <td className={`cv-dir lc-forte${tom(p.lucroLiquido)}`}>{r$(p.lucroLiquido)}</td>
                <td className={`cv-dir${tom(p.lucroExtraDoApp)}`}>{r$(p.lucroExtraDoApp)}</td>
                <td className={`cv-dir${tom(p.parteDaFarmacia)}`}>{r$(p.parteDaFarmacia)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
